// @ts-nocheck
import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import closeVariantModal from "@insite/client-framework/Store/Components/ProductSelector/Handlers/CloseVariantModal";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import updateVariantSelection from "@insite/client-framework/Store/Components/ProductSelector/Handlers/UpdateVariantSelection";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import { formGridProps } from "@store/Themes/common/props/form";
/* Starter Custom Code - End */

interface OwnProps {
    skipInventoryValidation?: boolean;
    extendedStyles?: ProductSelectorVariantModalStyles;
    skipProductPrice?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const { variantModalIsOpen, variantModalProductId, variantSelection, selectedVariantProductInfo } =
        getProductSelector(state);
    return {
        variantModalIsOpen,
        variantParentProduct: getProductState(state, variantModalProductId).value,
        variantChildren: getVariantChildrenDataView(state, variantModalProductId).value,
        selectedVariant: getProductState(state, selectedVariantProductInfo?.productId).value,
        selectedVariantProductInfo,
        variantSelection,
    };
};

const mapDispatchToProps = {
    closeVariantModal,
    updateVariantSelection,
    setProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorVariantModalStyles {
    modal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    variantSelect?: SelectProps;
    selectButton?: ButtonPresentationProps;
    // Starter Custom Code
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const productSelectorVariantModalStyles: ProductSelectorVariantModalStyles = {
    modal: { sizeVariant: "small" },
    gridContainer: formGridProps.gridContainer,
    gridItem: { width: 12 },
};

/**
 * @deprecated Use productSelectorVariantModalStyles instead.
 */
export const quickOrderVariantModalStyles = productSelectorVariantModalStyles;

const ProductSelectorVariantModal = ({
    variantModalIsOpen,
    variantParentProduct,
    variantChildren,
    selectedVariant,
    variantSelection,
    extendedStyles,
    closeVariantModal,
    updateVariantSelection,
    setProduct,
    selectedVariantProductInfo,
    skipInventoryValidation,
    skipProductPrice,
}: Props) => {
    const [styles] = useState(() => mergeToNew(quickOrderVariantModalStyles, extendedStyles));

    if (!variantParentProduct?.variantTraits || !variantChildren) {
        return null;
    }

    const closeModalHandler = () => {
        closeVariantModal();
    };

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        updateVariantSelection({ traitValueId, variantTraitId });
    };

    const selectButtonClickHandler = () => {
        setProduct({
            productId: variantParentProduct.id,
            variantId: selectedVariant?.id,
            validateProduct: true,
            skipInventoryValidation,
        });
        closeVariantModal();
    };

    const filteredVariantTraits = useFilteredVariantTraits(
        variantParentProduct.variantTraits,
        variantChildren,
        variantSelection,
    );

    return (
        <Modal
            {...styles.modal}
            headline={translate("Select options")}
            isOpen={variantModalIsOpen}
            handleClose={closeModalHandler}
            data-test-selector="modal_ProductSelectorVariantModal"
        >
            {/* Starter Custom Code - Start */}
            <GridContainer {...styles.gridContainer}>
                {filteredVariantTraits.map(variantTrait => (
                    <GridItem {...styles.gridItem} key={variantTrait.id.toString()}>
                        <Select
                            {...styles.variantSelect}
                            label={variantTrait.nameDisplay}
                            value={variantSelection[variantTrait.id]}
                            onChange={event => variantChangeHandler(event.currentTarget.value, variantTrait.id)}
                            data-test-selector={`productSelectorVariantModal_Dropdown_${variantTrait.nameDisplay}`}
                        >
                            <option value="">
                                {variantTrait.unselectedValue
                                    ? variantTrait.unselectedValue
                                    : `${translate("Select")} ${variantTrait.nameDisplay}`}
                            </option>
                            {variantTrait.traitValues?.map(traitValue => (
                                <option value={`${traitValue.id}`} key={`${traitValue.id}`}>
                                    {traitValue.valueDisplay}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                ))}
                {!skipProductPrice && selectedVariant && selectedVariantProductInfo && (
                    <GridItem {...styles.gridItem}>
                        <ProductPrice
                            product={{ product: selectedVariant, productInfo: selectedVariantProductInfo }}
                            showLabel={false}
                        />
                    </GridItem>
                )}
            </GridContainer>
            <FormActions fullWidth>
                <Button
                    {...styles.selectButton}
                    disabled={!selectedVariant || !selectedVariant.id}
                    onClick={selectButtonClickHandler}
                    data-test-selector="productSelectorVariantModal_selectButton"
                >
                    {translate("Select")}
                </Button>
            </FormActions>
            {/* Starter Custom Code - End */}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelectorVariantModal);
